import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Flex from 'components/common/Flex';
import { useAppContext } from 'providers/AppProvider';
import { Card } from 'react-bootstrap';
import * as echarts from 'echarts/core';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent
} from 'echarts/components';
import { PieChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from 'components/common/ReactEchart';
import { fetchKeyTypeComp } from 'hooks/CallApi';
import { formatCountStyle2 } from 'components/common/SunnyUtils';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const EncOptCompare = () => {
  const { getThemeColor } = useAppContext();
  const encryptionChartRef = useRef(null);
  const [apiData, setApiData] = useState({ api_cnt: 0, cf_cnt: 0, sdk_cnt: 0 });

  const {
    config: { dashboardDate, dashboardDateValue, Chart_startdate, Chart_enddate, isDemo },
  } = useAppContext();

  const [dateRange, setDateRange] = useState({
    start: Chart_startdate,
    end: Chart_enddate
  });
  useEffect(() => {
    setDateRange({
      start: Chart_startdate,
      end: Chart_enddate
    });
  }, [Chart_startdate, Chart_enddate]);

  useEffect(() => {
    let cancelled = false;
    const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fetchData = async () => {
      try {
        const response = await fetchKeyTypeComp({
          period: dashboardDateValue,
          start: dateRange.start,
          end: dateRange.end,
          timeZone: clientTimeZone,
          demo: isDemo
        });
        if (cancelled) return;
        const data = response.data[0];
        setApiData({ api_cnt: data.api_cnt, cf_cnt: data.cf_cnt, sdk_cnt: data.sdk_cnt });
      } catch (error) {
        if (cancelled) return;
        console.error('데이터 조회 에러:', error);
        setApiData({ api_cnt: 0, cf_cnt: 0, sdk_cnt: 0 });
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [dashboardDate, dateRange, isDemo]);

  const getOption = (titleText, apiValue, cloudValue, sdkValue, apiColor, cloudColor, sdkColor) => {
    const isDataEmpty = apiValue + cloudValue + sdkValue === 0;

    return {
      legend: {
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
        itemWidth: 10,
        itemHeight: 10,
        textStyle: {
          color: getThemeColor('gray-600'),
          fontSize: 12
        }
      },
      series: [
        {
          type: 'pie',
          radius: ['65%', '85%'],
          center: ['50%', '70%'],
          startAngle: 180,
          endAngle: 0,
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#FFFFFF',
            borderWidth: 2
          },
          label: {
            show: true,
            position: 'center',
            fontSize: 10,
            fontWeight: 'bold',
            color: getThemeColor('gray-700'),
            formatter: isDataEmpty ? `{b|${titleText}}\n{c|0건}` : `{b|${titleText}}\n{c|${formatCountStyle2(apiValue + cloudValue + sdkValue)}건}`,
            offset: [0, -5],
            rich: {
              b: {
                fontSize: 14,
                lineHeight: 20,
                color: '#64748B',
                fontWeight: '600'
              },
              c: {
                fontSize: 22,
                lineHeight: 28,
                fontWeight: '800',
                color: '#1E293B'
              }
            }
          },
          labelLine: { show: false },
          data: isDataEmpty
            ? [
              { value: 1, itemStyle: { color: '#f0f0f0' } },

            ]
            : [
              {
                value: apiValue,
                name: 'API',
                itemStyle: {
                  color: apiColor
                }
              },
              {
                value: cloudValue,
                name: 'CloudFunctions',
                itemStyle: {
                  color: cloudColor
                }
              },
              {
                value: sdkValue,
                name: 'SDK',
                itemStyle: {
                  color: sdkColor
                }
              }
            ]
        }
      ],
      tooltip:
        isDataEmpty
          ? { show: false } // 빈 데이터일 때는 툴팁 비활성화
          :
          {
            show: true,
            trigger: 'item',
            padding: [7, 10],
            backgroundColor: getThemeColor('gray-100'),
            borderColor: getThemeColor('gray-300'),
            textStyle: { color: getThemeColor('gray-1100') },
            borderWidth: 1,
            transitionDuration: 0,
            axisPointer: { type: 'none' }
          }
    };
  };

  return (
    <Card className="h-md-100" style={{ borderRadius: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', border: 'none', backgroundColor: '#FFFFFF' }}>
      <Card.Header className="pb-0 d-flex align-items-center" style={{ padding: '20px 24px', borderBottom: 'none', backgroundColor: 'transparent', flexWrap: 'wrap', gap: '8px' }}>
        <h5 style={{ margin: 0, fontWeight: 700, color: '#111827', wordBreak: 'keep-all' }}>인증키 타입별 요청 비교</h5>
        <span
          style={{
            marginLeft: '8px',
            marginTop: 0,
            marginBottom: 0,
            padding: '2px 8px',
            // backgroundColor: '#A1C4E3',
            backgroundColor: '#2C7BE5',
            color: 'white',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap'
          }}
        >
          {dashboardDate}
        </span>
      </Card.Header>

      <Card.Body as={Flex} className="pt-0" alignItems="center" justifyContent="space-between">
        <ReactEchart
          echarts={echarts}
          option={getOption(
            '인증키 타입',
            apiData.api_cnt,
            apiData.cf_cnt,
            apiData.sdk_cnt,
            '#F26C4F', // API
            '#10B981', // CF
            '#00B4D8'  // SDK
          )}
          ref={encryptionChartRef}
          style={{ width: '100%', height: '15rem' }}
        />
      </Card.Body>
    </Card>
  );
};

EncOptCompare.propTypes = {
  totalRequests: PropTypes.number,
  encryptionValue: PropTypes.number,
  decryptionValue: PropTypes.number
};

export default EncOptCompare;
